import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import apiClient from '../../utils/apiClient';
import ScenarioMiniCard from '../ScenarioMiniCard';
import TestScenarioDetail from '../TestScenarioDetail';
import { extractStepsByType } from '../../utils/testUtils';
import './TestCycleManager.css';

/**
 * ZephyrMented - Test Cycle Manager
 * Composant de gestion des cycles de tests basé sur les tests existants dans Neo4j
 */
const TestCycleManager = () => {
  const { t } = useTranslation();
  const [cycles, setCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [showTestDetailModal, setShowTestDetailModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    sprint: '',
    jiraEpic: '',
    tags: '',
    selectedTests: []
  });
  const [availableNodes, setAvailableNodes] = useState([]);
  const [loadingNodes, setLoadingNodes] = useState(false);
  const [creatingCycle, setCreatingCycle] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const normalizedTests = useMemo(() => {
    return tests.map(test => {
      let parsedDetails = test?.testDetails;

      if (typeof parsedDetails === 'string') {
        try {
          parsedDetails = JSON.parse(parsedDetails);
        } catch (error) {
          console.warn('⚠️ Impossible de parser testDetails:', error.message);
          parsedDetails = null;
        }
      }

      if (!parsedDetails && Array.isArray(test?.steps)) {
        parsedDetails = { steps: test.steps };
      }

      const displayName =
        test?.testName ||
        test?.title ||
        test?.name ||
        parsedDetails?.title ||
        parsedDetails?.name ||
        'Sans nom';

      const rawType =
        test?.testType ||
        test?.type ||
        parsedDetails?.type ||
        parsedDetails?.category ||
        'UNKNOWN';

      const displayType =
        typeof rawType === 'string'
          ? rawType
          : rawType != null
            ? String(rawType)
            : 'UNKNOWN';

      return {
        ...test,
        displayName,
        displayType,
        testDetails: parsedDetails || null
      };
    });
  }, [tests]);

  // Filtrer cycles basés sur le statut archivé
  const filteredCycles = useMemo(() => {
    if (!showArchived) {
      return cycles.filter(cycle => cycle.status !== 'archived');
    }
    return cycles;
  }, [cycles, showArchived]);

  useEffect(() => {
    fetchCycles();
  }, []);

  useEffect(() => {
    if (showCreateModal) {
      fetchAvailableNodes();
    }
  }, [showCreateModal]);

  /**
   * Récupérer la liste des cycles
   */
  const fetchCycles = async () => {
    try {
      const { data } = await apiClient.get('/api/test-cycles');
      setCycles(data.cycles);
    } catch (error) {
      console.error('Erreur chargement cycles:', error);
      toast.error(t('featureManager.testCycles.errors.loadCycles') || 'Erreur chargement cycles');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Récupérer les détails d'un cycle avec tests enrichis
   */
  const fetchCycleDetails = async (cycleId) => {
    setLoadingDetails(true);
    try {
      const { data } = await apiClient.get(`/api/test-cycles/${cycleId}`);
      setSelectedCycle(data.cycle);
      setTests(data.cycle.tests || []);
    } catch (error) {
      console.error('Erreur chargement détails cycle:', error);
      toast.error(t('featureManager.testCycles.errors.loadDetails') || 'Erreur chargement détails');
    } finally {
      setLoadingDetails(false);
    }
  };

  /**
   * Exécuter un test
   */
  const handleExecuteTest = async (nodeId, testId, status, notes = '') => {
    try {
      await apiClient.post(`/api/test-cycles/${selectedCycle._id}/execute`, {
        nodeId,
        testId,
        status,
        notes,
        duration: Math.floor(Math.random() * 300), // Simulé pour l'instant
        environment: 'staging'
      });

      toast.success(t('featureManager.testCycles.execution.success') || 'Test exécuté avec succès');
      
      // Rafraîchir les détails et la liste
      fetchCycleDetails(selectedCycle._id);
      fetchCycles();
    } catch (error) {
      console.error('Erreur exécution test:', error);
      toast.error(t('featureManager.testCycles.execution.error') || 'Erreur lors de l\'exécution');
    }
  };

  /**
   * Ouvrir le détail d'un test
   */
  const handleOpenTestDetail = (test) => {
    setSelectedTest(test);
    setShowTestDetailModal(true);
  };

  /**
   * Fermer la modal de détail
   */
  const handleCloseTestDetail = () => {
    setSelectedTest(null);
    setShowTestDetailModal(false);
  };

  /**
   * Archiver un cycle
   */
  const handleArchiveCycle = async (cycleId) => {
    if (!confirm(t('featureManager.testCycles.archive.confirmArchive'))) return;

    try {
      await apiClient.patch(`/api/test-cycles/${cycleId}/archive`);

      toast.success(t('featureManager.testCycles.archive.archived'));
      fetchCycles();
    } catch (error) {
      console.error('Erreur archivage cycle:', error);
      toast.error(t('featureManager.testCycles.archive.archiveError'));
    }
  };

  /**
   * Restaurer un cycle archivé
   */
  const handleRestoreCycle = async (cycleId) => {
    if (!confirm(t('featureManager.testCycles.archive.confirmRestore'))) return;

    try {
      await apiClient.patch(`/api/test-cycles/${cycleId}/restore`);

      toast.success(t('featureManager.testCycles.archive.restored'));
      fetchCycles();
    } catch (error) {
      console.error('Erreur restauration cycle:', error);
      toast.error(t('featureManager.testCycles.archive.restoreError'));
    }
  };

  /**
   * Exporter un cycle
   */
  const handleExportCycle = async (cycleId, format = 'json') => {
    try {
      toast.info(t('featureManager.testCycles.export.downloading'), { autoClose: 2000 });

      const response = await apiClient.get(`/api/test-cycles/${cycleId}/export?format=${format}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `cycle_${cycleId}_${Date.now()}.${format}`;
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(t('featureManager.testCycles.export.exported'));
    } catch (error) {
      console.error('Erreur export cycle:', error);
      toast.error(t('featureManager.testCycles.export.exportError'));
    }
  };

  /**
   * Suggérer les priorités avec IA
   */
  const handleSuggestPriorities = async () => {
    if (!selectedCycle) return;

    try {
      const { data } = await apiClient.post(`/api/test-cycles/${selectedCycle._id}/suggest-priority`);
      
      // Afficher les suggestions dans une modal ou un toast
      toast.success(
        `🤖 ${data.priorities.priorities.length} suggestions de priorités générées`,
        { autoClose: 5000 }
      );

      console.log('Priorités suggérées:', data.priorities);
      // TODO: Afficher modal avec les suggestions
    } catch (error) {
      console.error('Erreur suggestion priorités:', error);
      toast.error(t('featureManager.testCycles.ai.priorityError') || 'Erreur suggestion priorités');
    }
  };

  /**
   * Détecter les gaps de couverture
   */
  const handleDetectGaps = async () => {
    if (!selectedCycle) return;

    try {
      const { data } = await apiClient.get(`/api/test-cycles/${selectedCycle._id}/coverage-gaps`);
      
      if (data.gaps.length === 0) {
        toast.success(t('featureManager.testCycles.ai.noGaps') || '✅ Aucun gap détecté');
      } else {
        toast.warning(
          `⚠️ ${data.gaps.length} nœud(s) critique(s) non couvert(s)`,
          { autoClose: 5000 }
        );
        console.log('Gaps détectés:', data.gaps);
        // TODO: Afficher modal avec les gaps
      }
    } catch (error) {
      console.error('Erreur détection gaps:', error);
      toast.error(t('featureManager.testCycles.ai.gapsError') || 'Erreur détection gaps');
    }
  };

  /**
   * Récupérer les nœuds disponibles avec leurs tests
   */
  const fetchAvailableNodes = async () => {
    console.log('🚀 fetchAvailableNodes - Démarrage...');
    setLoadingNodes(true);
    try {
      const { data } = await apiClient.get('/nodes');
      console.log('📦 Data reçue:', data);
      console.log('📦 Nombre de nœuds:', data.length);
      
      // Filtrer les nœuds qui ont des tests
      const nodesWithTests = data.filter(node => {
        console.log('🔍 Checking node:', node.name, 'testsJson:', node.testsJson ? 'présent' : 'absent');
        if (!node.testsJson) return false;
        try {
          const tests = JSON.parse(node.testsJson);
          console.log('✅ Tests parsés pour', node.name, ':', tests.length, 'tests');
          return Array.isArray(tests) && tests.length > 0;
        } catch (e) {
          console.error('❌ Erreur parsing testsJson pour', node.name, ':', e.message);
          return false;
        }
      }).map(node => {
        const tests = JSON.parse(node.testsJson);
        console.log('📊 Nœud:', node.name);
        console.log('📊 Node ID:', node.id, 'Type:', typeof node.id);
        console.log('📊 Tests bruts:', tests);
        console.log('📊 Premier test complet:', JSON.stringify(tests[0], null, 2));
        if (tests[0] && tests[0].steps) {
          console.log('📊 Steps détaillés:', JSON.stringify(tests[0].steps, null, 2));
        }
        return {
          ...node,
          tests
        };
      });

      console.log('✅ Nœuds avec tests:', nodesWithTests.length);
      setAvailableNodes(nodesWithTests);
    } catch (error) {
      console.error('Erreur chargement nœuds:', error);
      toast.error(t('featureManager.testCycles.errors.loadNodes') || 'Erreur chargement nœuds');
    } finally {
      setLoadingNodes(false);
    }
  };

  /**
   * Gérer le changement du formulaire
   */
  const handleFormChange = (field, value) => {
    setCreateForm(prev => ({ ...prev, [field]: value }));
  };

  /**
   * Basculer la sélection d'un test
   */
  const toggleTestSelection = (nodeId, testId, testName) => {
    setCreateForm(prev => {
      const exists = prev.selectedTests.find(t => t.nodeId === nodeId && t.testId === testId);
      
      if (exists) {
        // Désélectionner
        return {
          ...prev,
          selectedTests: prev.selectedTests.filter(t => !(t.nodeId === nodeId && t.testId === testId))
        };
      } else {
        // Sélectionner
        return {
          ...prev,
          selectedTests: [...prev.selectedTests, { nodeId, testId, testName, priority: 3 }]
        };
      }
    });
  };

  /**
   * Créer un nouveau cycle
   */
  const handleCreateCycle = async () => {
    // Validation
    if (!createForm.name.trim()) {
      toast.error(t('featureManager.testCycles.create.nameRequired') || 'Le nom est requis');
      return;
    }

    if (createForm.selectedTests.length === 0) {
      toast.error(t('featureManager.testCycles.create.testsRequired') || 'Sélectionnez au moins un test');
      return;
    }

    setCreatingCycle(true);
    try {
      const payload = {
        name: createForm.name.trim(),
        description: createForm.description.trim(),
        startDate: createForm.startDate || undefined,
        endDate: createForm.endDate || undefined,
        nodeTestPairs: createForm.selectedTests, // Renommé pour correspondre au backend
        metadata: {
          sprint: createForm.sprint.trim() || undefined,
          jiraEpic: createForm.jiraEpic.trim() || undefined,
          tags: createForm.tags.trim() ? createForm.tags.split(',').map(t => t.trim()) : []
        }
      };

      console.log('📤 Payload envoyé au backend:', JSON.stringify(payload, null, 2));
      console.log('📤 Nombre de tests sélectionnés:', createForm.selectedTests.length);
      console.log('📤 Tests détaillés:', createForm.selectedTests);

      const { data } = await apiClient.post('/api/test-cycles', payload);
      toast.success(t('featureManager.testCycles.create.success') || 'Cycle créé avec succès !');
      
      // Réinitialiser le formulaire
      setCreateForm({
        name: '',
        description: '',
        startDate: '',
        endDate: '',
        sprint: '',
        jiraEpic: '',
        tags: '',
        selectedTests: []
      });
      setShowCreateModal(false);
      
      // Rafraîchir la liste
      fetchCycles();
    } catch (error) {
      console.error('Erreur création cycle:', error);
      toast.error(t('featureManager.testCycles.create.error') || 'Erreur lors de la création');
    } finally {
      setCreatingCycle(false);
    }
  };

  /**
   * Obtenir la couleur du statut
   */
  const getStatusColor = (status) => {
    const colors = {
      passed: '#4caf50',
      failed: '#f44336',
      blocked: '#ff9800',
      not_run: '#9e9e9e',
      in_progress: '#2196f3',
      skipped: '#9e9e9e'
    };
    return colors[status] || colors.not_run;
  };

  /**
   * Obtenir le label du statut
   */
  const getStatusLabel = (status) => {
    if (!status) return t('featureManager.testCycles.status.notRun') || 'Not Run';
    
    const labels = {
      passed: t('featureManager.testCycles.status.passed') || 'Passed',
      failed: t('featureManager.testCycles.status.failed') || 'Failed',
      blocked: t('featureManager.testCycles.status.blocked') || 'Blocked',
      not_run: t('featureManager.testCycles.status.notRun') || 'Not Run',
      in_progress: t('featureManager.testCycles.status.inProgress') || 'In Progress',
      skipped: t('featureManager.testCycles.status.skipped') || 'Skipped'
    };
    return labels[status] || status;
  };

  /**
   * Formater la date
   */
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="test-cycle-loading">
        <div className="spinner"></div>
        <p>{t('featureManager.testCycles.loading') || 'Chargement...'}</p>
      </div>
    );
  }

  return (
    <div className="test-cycle-manager">
      {/* Sidebar - Liste des cycles */}
      <div className="cycles-sidebar">
        <div className="sidebar-header">
          <h2>🔄 {t('featureManager.testCycles.title') || 'Cycles de Tests'}</h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button 
              className="btn-toggle-archived"
              onClick={() => setShowArchived(!showArchived)}
              style={{ 
                padding: '6px 12px', 
                fontSize: '12px',
                background: showArchived ? '#666' : '#e0e0e0',
                color: showArchived ? 'white' : '#333'
              }}
            >
              {showArchived 
                ? `👁️ ${t('featureManager.testCycles.archive.hideArchived')}`
                : `📦 ${t('featureManager.testCycles.archive.showArchived')}`
              }
            </button>
            <button 
              className="btn-new-cycle"
              onClick={() => setShowCreateModal(true)}
            >
              ➕ {t('featureManager.testCycles.actions.new') || 'Nouveau'}
            </button>
          </div>
        </div>

        <div className="cycles-list">
          {filteredCycles.length === 0 ? (
            <div className="empty-cycles">
              <p>{t('featureManager.testCycles.empty') || 'Aucun cycle de tests'}</p>
              <button 
                className="btn-create-first"
                onClick={() => setShowCreateModal(true)}
              >
                {t('featureManager.testCycles.actions.createFirst') || 'Créer le premier cycle'}
              </button>
            </div>
          ) : (
            filteredCycles.map(cycle => (
              <div
                key={cycle._id}
                className={`cycle-card ${selectedCycle?._id === cycle._id ? 'active' : ''} ${cycle.status === 'archived' ? 'archived' : ''}`}
                onClick={() => fetchCycleDetails(cycle._id)}
              >
                <div className="cycle-card-header">
                  <h3>{cycle.name}</h3>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    {cycle.status === 'archived' && (
                      <span className="archived-badge">
                        {t('featureManager.testCycles.archive.archivedBadge')}
                      </span>
                    )}
                    <span className={`status-badge status-${cycle.status}`}>
                      {t(`featureManager.testCycles.cycleStatus.${cycle.status}`) || cycle.status}
                    </span>
                  </div>
                </div>
                
                <p className="cycle-dates">
                  📅 {formatDate(cycle.startDate)} → {formatDate(cycle.endDate)}
                </p>

                {cycle.stats && (
                  <>
                    <div className="cycle-stats">
                      <span className="stat">📝 {cycle.stats.totalTests}</span>
                      <span className="stat passed">✅ {cycle.stats.passed}</span>
                      <span className="stat failed">❌ {cycle.stats.failed}</span>
                      <span className="stat not-run">⏸️ {cycle.stats.notRun}</span>
                    </div>

                    <div className="progress-bar-container">
                      <div 
                        className="progress-bar-fill"
                        style={{
                          width: `${cycle.stats.completionRate || 0}%`,
                          background: cycle.stats.passRate > 80 ? '#4caf50' : 
                                     cycle.stats.passRate > 50 ? '#ff9800' : '#f44336'
                        }}
                      />
                    </div>

                    <div className="cycle-meta">
                      <small>
                        {t('featureManager.testCycles.completion') || 'Complétion'}: {cycle.stats.completionRate || 0}% | 
                        {t('featureManager.testCycles.passRate') || 'Taux de passage'}: {cycle.stats.passRate || 0}%
                      </small>
                    </div>
                  </>
                )}

                {/* Boutons d'actions cycle */}
                <div className="cycle-card-actions" onClick={(e) => e.stopPropagation()} style={{ marginTop: '12px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {cycle.status === 'archived' ? (
                    <button
                      className="btn-restore-cycle"
                      onClick={() => handleRestoreCycle(cycle._id)}
                      style={{ fontSize: '11px', padding: '4px 8px', background: '#4caf50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      ♻️ {t('featureManager.testCycles.actions.restore')}
                    </button>
                  ) : (
                    <button
                      className="btn-archive-cycle"
                      onClick={() => handleArchiveCycle(cycle._id)}
                      style={{ fontSize: '11px', padding: '4px 8px', background: '#9e9e9e', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      📦 {t('featureManager.testCycles.actions.archive')}
                    </button>
                  )}
                  <button
                    className="btn-export-cycle"
                    onClick={() => {
                      const format = prompt(t('featureManager.testCycles.export.format') + ' (json/csv/markdown):', 'json');
                      if (format && ['json', 'csv', 'markdown'].includes(format.toLowerCase())) {
                        handleExportCycle(cycle._id, format.toLowerCase());
                      }
                    }}
                    style={{ fontSize: '11px', padding: '4px 8px', background: '#2196f3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    📤 {t('featureManager.testCycles.actions.export')}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main - Détails du cycle sélectionné */}
      <div className="cycle-details">
        {selectedCycle ? (
          <>
            <div className="cycle-header">
              <div className="header-info">
                <h1>{selectedCycle.name}</h1>
                <p className="description">{selectedCycle.description}</p>
                <div className="header-meta">
                  <span>📅 {formatDate(selectedCycle.startDate)} → {formatDate(selectedCycle.endDate)}</span>
                  {selectedCycle.assignedTo && (
                    <span>👤 {selectedCycle.assignedTo}</span>
                  )}
                  <span className={`status-badge-large status-${selectedCycle.status}`}>
                    {t(`featureManager.testCycles.cycleStatus.${selectedCycle.status}`) || selectedCycle.status}
                  </span>
                </div>
              </div>
              
              <div className="cycle-actions">
                <button 
                  className="btn-ai-priority"
                  onClick={handleSuggestPriorities}
                >
                  🤖 {t('featureManager.testCycles.actions.suggestPriorities') || 'Suggérer Priorités'}
                </button>
                <button 
                  className="btn-ai-gaps"
                  onClick={handleDetectGaps}
                >
                  🔍 {t('featureManager.testCycles.actions.detectGaps') || 'Détecter Gaps'}
                </button>
              </div>
            </div>

            {loadingDetails ? (
              <div className="details-loading">
                <div className="spinner"></div>
                <p>{t('featureManager.testCycles.loadingDetails') || 'Chargement des tests...'}</p>
              </div>
            ) : (
              <div className="tests-table-container">
                <table className="tests-table">
                  <thead>
                    <tr>
                      <th>{t('featureManager.testCycles.table.node') || 'Nœud'}</th>
                      <th>{t('featureManager.testCycles.table.test') || 'Test'}</th>
                      <th>{t('featureManager.testCycles.table.type') || 'Type'}</th>
                      <th>{t('featureManager.testCycles.table.priority') || 'Priorité'}</th>
                      <th>{t('featureManager.testCycles.table.status') || 'Statut'}</th>
                      <th>{t('featureManager.testCycles.table.executedBy') || 'Exécuté par'}</th>
                      <th>{t('featureManager.testCycles.table.actions') || 'Actions'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {normalizedTests.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="empty-tests">
                          {t('featureManager.testCycles.noTests') || 'Aucun test dans ce cycle'}
                        </td>
                      </tr>
                    ) : (
                      normalizedTests.map(test => (
                        <tr key={`${test.nodeId}_${test.testId}`}>
                          <td>
                            <div className="node-cell">
                              <strong>{test.nodeName}</strong>
                              <small className="node-type">{test.nodeType}</small>
                            </div>
                          </td>
                          <td>
                            <div className="test-cell">
                              <strong 
                                className="test-name-link"
                                onClick={() => handleOpenTestDetail(test)}
                                style={{ cursor: 'pointer', color: '#3b82f6' }}
                                title={t('featureManager.testCycles.actions.viewDetails') || 'Cliquer pour voir les détails'}
                              >
                                {test.displayName}
                              </strong>
                              {test.testDetails?.steps && test.testDetails.steps.length > 0 && (
                                <small className="test-preview">
                                  {test.testDetails.steps[0].given || 
                                   test.testDetails.steps[0].when || 
                                   test.testDetails.steps[0].then || ''}
                                </small>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className="type-badge">{test.displayType}</span>
                          </td>
                          <td>
                            <span className={`priority-badge priority-${test.priority}`}>
                              {test.priority}
                            </span>
                          </td>
                          <td>
                            <span
                              className="status-badge"
                              style={{ background: getStatusColor(test.execution?.status) }}
                            >
                              {getStatusLabel(test.execution?.status)}
                            </span>
                          </td>
                          <td className="executed-by">
                            {test.execution?.executedByName || test.execution?.executedBy || '-'}
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="btn-pass"
                                onClick={() => handleExecuteTest(
                                  test.nodeId,
                                  test.testId,
                                  'passed',
                                  t('featureManager.testCycles.execution.passedNote') || 'Test passé avec succès'
                                )}
                                title={t('featureManager.testCycles.actions.pass') || 'Passer'}
                              >
                                ✅
                              </button>
                              <button
                                className="btn-fail"
                                onClick={() => {
                                  const notes = prompt(
                                    t('featureManager.testCycles.execution.failPrompt') || 'Notes sur l\'échec:'
                                  );
                                  if (notes) {
                                    handleExecuteTest(
                                      test.nodeId,
                                      test.testId,
                                      'failed',
                                      notes
                                    );
                                  }
                                }}
                                title={t('featureManager.testCycles.actions.fail') || 'Échouer'}
                              >
                                ❌
                              </button>
                              <button
                                className="btn-block"
                                onClick={() => handleExecuteTest(
                                  test.nodeId,
                                  test.testId,
                                  'blocked',
                                  t('featureManager.testCycles.execution.blockedNote') || 'Test bloqué'
                                )}
                                title={t('featureManager.testCycles.actions.block') || 'Bloquer'}
                              >
                                🚫
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">🔄</div>
            <h2>{t('featureManager.testCycles.selectCycle') || 'Sélectionnez un cycle de tests'}</h2>
            <p>{t('featureManager.testCycles.selectCycleDesc') || 'Ou créez-en un nouveau pour commencer'}</p>
            <button 
              className="btn-create-large"
              onClick={() => setShowCreateModal(true)}
            >
              ➕ {t('featureManager.testCycles.actions.createNew') || 'Créer un cycle'}
            </button>
          </div>
        )}
      </div>

      {/* TODO: Modal de création de cycle */}
      {showCreateModal && (
        <div className="modal-backdrop" onClick={() => setShowCreateModal(false)}>
          <div className="create-cycle-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🔄 {t('featureManager.testCycles.create.title') || 'Créer un cycle de tests'}</h2>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              {/* Informations générales */}
              <div className="form-section">
                <h3>{t('featureManager.testCycles.create.generalInfo') || 'Informations générales'}</h3>
                
                <div className="form-group">
                  <label>{t('featureManager.testCycles.create.name') || 'Nom du cycle'} *</label>
                  <input
                    type="text"
                    value={createForm.name}
                    onChange={e => handleFormChange('name', e.target.value)}
                    placeholder={t('featureManager.testCycles.create.namePlaceholder') || 'Ex: Sprint 24 - Tests Critiques'}
                  />
                </div>

                <div className="form-group">
                  <label>{t('featureManager.testCycles.create.description') || 'Description'}</label>
                  <textarea
                    value={createForm.description}
                    onChange={e => handleFormChange('description', e.target.value)}
                    placeholder={t('featureManager.testCycles.create.descriptionPlaceholder') || 'Objectif de ce cycle de tests...'}
                    rows={3}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('featureManager.testCycles.create.startDate') || 'Date de début'} *</label>
                    <input
                      type="date"
                      value={createForm.startDate}
                      onChange={e => handleFormChange('startDate', e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('featureManager.testCycles.create.endDate') || 'Date de fin'} *</label>
                    <input
                      type="date"
                      value={createForm.endDate}
                      onChange={e => handleFormChange('endDate', e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Métadonnées */}
              <div className="form-section">
                <h3>{t('featureManager.testCycles.create.metadata') || 'Métadonnées'}</h3>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('featureManager.testCycles.create.sprint') || 'Sprint'}</label>
                    <input
                      type="text"
                      value={createForm.sprint}
                      onChange={e => handleFormChange('sprint', e.target.value)}
                      placeholder={t('featureManager.testCycles.create.sprintPlaceholder') || 'Ex: Sprint 24'}
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('featureManager.testCycles.create.jiraEpic') || 'Epic JIRA'}</label>
                    <input
                      type="text"
                      value={createForm.jiraEpic}
                      onChange={e => handleFormChange('jiraEpic', e.target.value)}
                      placeholder={t('featureManager.testCycles.create.jiraEpicPlaceholder') || 'Ex: PROJ-123'}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('featureManager.testCycles.create.tags') || 'Tags (séparés par virgule)'}</label>
                  <input
                    type="text"
                    value={createForm.tags}
                    onChange={e => handleFormChange('tags', e.target.value)}
                    placeholder={t('featureManager.testCycles.create.tagsPlaceholder') || 'Ex: critique, régression, api'}
                  />
                </div>
              </div>

              {/* Sélection des tests */}
              <div className="form-section">
                <h3>
                  {t('featureManager.testCycles.create.selectTests') || 'Sélection des tests'} 
                  <span className="test-count">({createForm.selectedTests.length} {t('featureManager.testCycles.create.selected') || 'sélectionnés'})</span>
                </h3>

                {loadingNodes ? (
                  <div className="loading-nodes">
                    ⏳ {t('featureManager.testCycles.create.loadingNodes') || 'Chargement des nœuds...'}
                  </div>
                ) : availableNodes.length === 0 ? (
                  <div className="no-nodes">
                    ℹ️ {t('featureManager.testCycles.create.noNodes') || 'Aucun nœud avec tests disponible'}
                  </div>
                ) : (
                  <div className="nodes-list">
                    {availableNodes.map(node => (
                      <div key={node.id} className="node-card">
                        <div className="node-header">
                          <strong>{node.name}</strong>
                          <span className="node-type">{node.type}</span>
                        </div>
                        <div className="tests-list">
                          {node.tests.map((test, idx) => {
                            const testId = test.id || `test-${idx}`;
                            const testName = test.name || test.title || `Test #${idx + 1}`;
                            const testType = test.type || test.category || 'Test';
                            
                            const isSelected = createForm.selectedTests.some(
                              t => t.nodeId === node.id && t.testId === testId
                            );
                            
                            // Formater les steps pour être compatibles avec ScenarioMiniCard
                            let formattedSteps = [];
                            if (test.steps && Array.isArray(test.steps)) {
                              formattedSteps = test.steps.map(step => {
                                // Si le step est déjà au bon format {type, text}
                                if (step.type && step.text) {
                                  return step;
                                }
                                // Si le step a given/when/then directement
                                if (step.given) return { type: 'Given', text: step.given };
                                if (step.when) return { type: 'When', text: step.when };
                                if (step.then) return { type: 'Then', text: step.then };
                                
                                // Si c'est une string, essayer de parser
                                if (typeof step === 'string') {
                                  const match = step.match(/^(Given|When|Then|And|But)\s+(.+)$/i);
                                  if (match) {
                                    return { type: match[1], text: match[2] };
                                  }
                                  return { type: 'Step', text: step };
                                }
                                return { type: 'Step', text: JSON.stringify(step) };
                              });
                            }
                            
                            console.log('🔄 Formatted steps for test', testName, ':', formattedSteps);
                            
                            // Créer un objet scénario à partir du test (même logique que PendingNodesPanel)
                            const scenario = {
                              id: testId,
                              title: testName,
                              type: testType,
                              positive: test.positive !== false,
                              steps: formattedSteps,
                              targetNode: node.name,
                              description: test.description,
                              priority: test.priority,
                              author: test.author
                            };
                            
                            return (
                              <div
                                key={testId}
                                className={`test-item-card ${isSelected ? 'selected' : ''}`}
                              >
                                <div className="test-item-header">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleTestSelection(node.id, testId, testName)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <div className="test-item-title">
                                    <span className="test-name-label">{testName}</span>
                                    <span className="test-type-badge">{testType}</span>
                                  </div>
                                </div>
                                
                                {/* Affichage du scénario avec ScenarioMiniCard */}
                                <div 
                                  className="test-scenario-preview"
                                  onClick={() => toggleTestSelection(node.id, testId, testName)}
                                >
                                  <ScenarioMiniCard scenario={scenario} />
                                </div>
                                
                                {/* Métadonnées du test */}
                                {(test.author || test.priority) && (
                                  <div className="test-meta-footer">
                                    {test.author && <span className="meta-author">� {test.author}</span>}
                                    {test.priority && (
                                      <span className={`meta-priority priority-${test.priority}`}>
                                        {test.priority === 'high' ? '� High' : test.priority === 'medium' ? '⚠️ Medium' : '✓ Normal'}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                {t('common.cancel') || 'Annuler'}
              </button>
              <button
                className="btn-primary"
                onClick={handleCreateCycle}
                disabled={creatingCycle || !createForm.name.trim() || createForm.selectedTests.length === 0}
              >
                {creatingCycle ? (
                  <>⏳ {t('featureManager.testCycles.create.creating') || 'Création...'}</>
                ) : (
                  <>✅ {t('featureManager.testCycles.create.create') || 'Créer le cycle'}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de détail du test */}
      {showTestDetailModal && selectedTest && (
        <TestScenarioDetail
          scenario={{
            id: selectedTest.testId || selectedTest.id,
            title: selectedTest.displayName,
            description: selectedTest.testDetails?.description || selectedTest.description || '',
            type: selectedTest.displayType,
            status: selectedTest.execution?.status || 'pending',
            nodeName: selectedTest.nodeName,
            steps: selectedTest.testDetails?.steps || selectedTest.steps || [],
            ...selectedTest.testDetails,
            ...selectedTest
          }}
          extractStepsByType={extractStepsByType}
          onClose={handleCloseTestDetail}
        />
      )}
    </div>
  );
};

export default TestCycleManager;

